import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Users, Calendar, CheckCircle, TrendingUp, Clock,
  Award, Target, Activity, AlertCircle, ArrowRight,
  UserCheck, BarChart3, Briefcase, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ManagerDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    team: { total: 0, present: 0, onLeave: 0, absent: 0 },
    performance: { avgScore: 0, topPerformers: 0, needsAttention: 0 },
    approvals: { pending: 0, thisWeek: 0 },
    tasks: { assigned: 0, completed: 0, overdue: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  useEffect(() => {
    fetchManagerStats();
    fetchTeamMembers();
    fetchPendingApprovals();
  }, []);

  const fetchManagerStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch team stats
      const teamRes = await axios.get(`${API}/manager/team-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { total: 8, present: 7, onLeave: 1, absent: 0 } }));
      
      setStats({
        team: teamRes.data,
        performance: { avgScore: 87, topPerformers: 3, needsAttention: 1 },
        approvals: { pending: 5, thisWeek: 12 },
        tasks: { assigned: 24, completed: 18, overdue: 2 }
      });
    } catch (error) {
      console.error('Failed to fetch manager stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    const members = [
      { name: 'Rajesh Kumar', role: 'Senior Developer', status: 'present', performance: 92 },
      { name: 'Priya Sharma', role: 'Developer', status: 'present', performance: 88 },
      { name: 'Amit Singh', role: 'QA Engineer', status: 'on_leave', performance: 85 },
      { name: 'Sneha Patel', role: 'Developer', status: 'present', performance: 90 }
    ];
    setTeamMembers(members);
  };

  const fetchPendingApprovals = async () => {
    const approvals = [
      { type: 'Leave', employee: 'Rajesh Kumar', description: 'Annual leave - 3 days', time: '2 hours ago' },
      { type: 'Timesheet', employee: 'Priya Sharma', description: 'Week 51 timesheet', time: '5 hours ago' },
      { type: 'Expense', employee: 'Amit Singh', description: 'Client meeting expenses', time: '1 day ago' }
    ];
    setPendingApprovals(approvals);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, link }) => (
    <Link to={link} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">{value}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center text-sm">
          <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
          <span className="text-green-600 font-medium">{trend}</span>
        </div>
      )}
    </Link>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-8">
          <h1 className="text-3xl font-bold mb-2">Manager Dashboard 🎯</h1>
          <p className="text-green-100">Lead your team to success</p>
        </div>

        <div className="px-8 py-6 -mt-8">
          {/* Team Overview */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Team Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Team Size"
                value={stats.team.total}
                subtitle={`${stats.team.present} present today`}
                icon={Users}
                color="bg-blue-600"
                link="/employees"
              />
              <StatCard
                title="Team Performance"
                value={`${stats.performance.avgScore}%`}
                subtitle={`${stats.performance.topPerformers} top performers`}
                icon={Award}
                color="bg-green-600"
                trend="+5% this month"
                link="/performance"
              />
              <StatCard
                title="Pending Approvals"
                value={stats.approvals.pending}
                subtitle={`${stats.approvals.thisWeek} this week`}
                icon={CheckCircle}
                color="bg-orange-600"
                link="/approvals"
              />
              <StatCard
                title="Team Tasks"
                value={stats.tasks.assigned}
                subtitle={`${stats.tasks.completed} completed`}
                icon={Target}
                color="bg-purple-600"
                link="/tasks"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Manager Actions</h2>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/approvals" className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all">
                  <CheckCircle className="w-8 h-8 text-blue-600 mb-2" />
                  <h3 className="font-semibold text-gray-900">Approve Requests</h3>
                  <p className="text-sm text-gray-500">{stats.approvals.pending} pending</p>
                </Link>
                <Link to="/attendance" className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all">
                  <Calendar className="w-8 h-8 text-green-600 mb-2" />
                  <h3 className="font-semibold text-gray-900">Team Attendance</h3>
                  <p className="text-sm text-gray-500">View & manage</p>
                </Link>
                <Link to="/performance" className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all">
                  <BarChart3 className="w-8 h-8 text-purple-600 mb-2" />
                  <h3 className="font-semibold text-gray-900">Performance Review</h3>
                  <p className="text-sm text-gray-500">Track & review</p>
                </Link>
                <Link to="/tasks" className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all">
                  <Target className="w-8 h-8 text-orange-600 mb-2" />
                  <h3 className="font-semibold text-gray-900">Assign Tasks</h3>
                  <p className="text-sm text-gray-500">Manage workload</p>
                </Link>
              </div>
            </div>

            {/* Pending Approvals */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Approvals</h2>
                <Link to="/approvals" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  View All
                </Link>
              </div>
              <div className="space-y-3">
                {pendingApprovals.map((approval, idx) => (
                  <div key={idx} className="p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-semibold text-gray-900 text-sm">{approval.type}</p>
                      <Clock className="w-4 h-4 text-orange-600" />
                    </div>
                    <p className="text-sm text-gray-600">{approval.employee}</p>
                    <p className="text-xs text-gray-500 mt-1">{approval.description}</p>
                    <p className="text-xs text-gray-400 mt-2">{approval.time}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
              <Link to="/employees" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                View All
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {teamMembers.map((member, idx) => (
                <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      member.status === 'present' ? 'bg-green-100 text-green-800' :
                      member.status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {member.status === 'present' ? 'Present' : member.status === 'on_leave' ? 'On Leave' : 'Absent'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">{member.role}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Performance</span>
                    <span className="text-sm font-semibold text-green-600">{member.performance}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ManagerDashboard;
